import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, tl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { BadgeCheck, PhoneCall, Loader2, Star } from "lucide-react";
import { STATUS_LABELS } from "@/lib/categories";
import { UstaProfileForm } from "@/components/UstaProfileForm";
import { toast } from "sonner";

export default function Account() {
  const { user, refreshUser, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "profile";
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [favs, setFavs] = useState({ jobs: [], ustalar: [] });
  const [notifs, setNotifs] = useState([]);
  const [ustaProfile, setUstaProfile] = useState(undefined);
  const [reviewFor, setReviewFor] = useState(null);
  const [confirmName, setConfirmName] = useState(false);
  const nameLocked = (user?.name_change_count || 0) >= 1;

  useEffect(() => { setName(user?.name || ""); setVerifyEmail(user?.email || ""); }, [user]);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);
  useEffect(() => {
    api.get("/jobs/mine").then(({ data }) => setJobs(data)).catch(() => {});
    api.get("/offers/mine").then(({ data }) => setOffers(data)).catch(() => {});
    api.get("/unlocks/mine").then(({ data }) => setUnlocks(data)).catch(() => {});
    api.get("/favorites").then(({ data }) => setFavs(data)).catch(() => {});
    api.get("/notifications").then(({ data }) => { setNotifs(data.items); api.post("/notifications/read"); }).catch(() => {});
    if (user?.id) api.get(`/ustalar/${user.id}`).then(({ data }) => setUstaProfile(data)).catch(() => setUstaProfile(null));
  }, [user]);

  const saveProfile = async () => {
    if (!/^[A-Za-zÇĞİıÖöŞşÜüâîûÂÎÛ]+( [A-Za-zÇĞİıÖöŞşÜüâîûÂÎÛ]+)*$/.test(name.trim())) {
      toast.error("Ad Soyad yalnızca harf ve tek boşluk içerebilir"); return;
    }
    setConfirmName(true);
  };
  const doSaveProfile = async () => {
    try {
      await api.put("/users/me", { name: name.trim() });
      await refreshUser();
      toast.success("Ad ve soyadınız başarıyla güncellendi. Düzenleme hakkınız tamamlanmıştır.");
      setConfirmName(false);
    } catch (e) { setConfirmName(false); toast.error(apiError(e)); }
  };
  const hasUstaProfile = ustaProfile && (ustaProfile.specialty || (ustaProfile.categories || []).length > 0);

  const requestPhone = async () => {
    try {
      const { data } = await api.post("/auth/phone/request", { phone, email: verifyEmail });
      setOtpSent(true); setResendIn(60);
      toast.success(`Doğrulama kodu ${verifyEmail} adresine gönderildi`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const verifyPhone = async () => {
    try { await api.post("/auth/phone/verify", { phone, code: otp }); await refreshUser(); setOtpSent(false); toast.success("Telefon doğrulandı ✓"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteAccount = async () => {
    try { await api.delete("/users/me"); toast.success("Hesabınız silindi"); logout(); navigate("/"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setTab = (t) => setParams({ tab: t });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-extrabold text-slate-900 mb-6">Hesabım</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto bg-white rounded-2xl p-1 border border-slate-200/80">
          {[["profile","Profil"],["phone","Telefon Doğrulama"],["usta","Usta Profili"],["jobs","İlanlarım"],["offers","Tekliflerim"],["unlocks","İletişimlerim"],["favorites","Favorilerim"],["notifications","Bildirimler"],["settings","Ayarlar"]].map(([v,l]) => (
            <TabsTrigger key={v} value={v} data-testid={`account-tab-${v}`} className="rounded-xl data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="mt-5">
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-lg space-y-4">
            <div className="flex items-center gap-4" data-testid="profile-avatar-section">
              <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "U")}&background=E2E8F0&color=64748B`} alt="" className="h-20 w-20 rounded-full object-cover border border-slate-200" data-testid="profile-avatar-img" />
              <p className="text-xs text-slate-400 leading-relaxed">Profil fotoğrafı yalnızca <b>Usta Profili</b> için geçerlidir ve yönetici onayından sonra vitrininizde yayınlanır. Normal profilde varsayılan avatar kullanılır.</p>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">Ad Soyad {nameLocked && <span className="text-slate-400">🔒</span>}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={nameLocked} className={`rounded-xl mt-1 ${nameLocked ? "bg-slate-100 text-slate-400" : ""}`} data-testid="profile-name-input" />
              {nameLocked ? (
                <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid="name-locked-note">🔒 Güvenlik politikalarımız ve kötüye kullanımı engellemek adına ad-soyad değiştirme hakkınız dolmuştur. Zorunlu değişiklik talepleriniz için lütfen destek ekibiyle iletişime geçiniz.</p>
              ) : (
                <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3 leading-relaxed" data-testid="name-warning-note">⚠️ <b>Önemli Bilgilendirme:</b> Platformumuzda güvenli bir hizmet ortamı sağlamak ve kötüye kullanımı engellemek adına ad-soyad değişikliği yalnızca <b>1 (bir) defaya mahsus</b> yapılabilmektedir. Lütfen bilgilerinizi dikkatle kontrol ediniz.</div>
              )}
            </div>
            <div><Label>E-posta</Label><Input value={user?.email} disabled className="rounded-xl mt-1 bg-slate-50" /></div>
            <div className="flex items-center gap-2 text-sm">
              {user?.email_verified ? <span className="text-emerald-600 flex items-center gap-1"><BadgeCheck className="h-4 w-4" /> E-posta doğrulandı</span> : <span className="text-slate-400">E-posta doğrulanmadı</span>}
            </div>
            {!hasUstaProfile && <span className="inline-block text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full" data-testid="not-usta-badge">Usta Değil</span>}
            <Button onClick={saveProfile} disabled={nameLocked} className="brand-gradient text-white rounded-xl" data-testid="profile-save-button">Kaydet</Button>
          </div>

          <AlertDialog open={confirmName} onOpenChange={setConfirmName}>
            <AlertDialogContent className="rounded-2xl" data-testid="name-confirm-modal">
              <AlertDialogHeader>
                <AlertDialogTitle>Ad ve Soyad Bilginizi Onaylıyor musunuz?</AlertDialogTitle>
                <AlertDialogDescription>
                  Yeni ad ve soyadınız '<b className="text-slate-900">{name.trim()}</b>' olarak güncellenecektir. Platform güvenliği ve kötüye kullanımı önleme politikamız gereği bu işlemi <b>yalnızca 1 kez</b> yapabilirsiniz. Onayladıktan sonra ad ve soyadınızı bir daha değiştiremezsiniz.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="name-confirm-cancel">Kontrol Et / Vazgeç</AlertDialogCancel>
                <AlertDialogAction onClick={doSaveProfile} className="brand-gradient text-white" data-testid="name-confirm-ok">Anladım, Onaylıyorum</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="phone" className="mt-5">
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-lg space-y-4">
            {user?.phone_verified ? (
              <div className="text-emerald-600 flex items-center gap-2 font-semibold"><BadgeCheck className="h-5 w-5" /> Telefon numarası doğrulandı ✓</div>
            ) : (
              <>
                <div><Label>Telefon Numarası</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+90 5xx xxx xx xx" className="rounded-xl mt-1" data-testid="phone-input" /></div>
                <div><Label>E-Posta Adresi</Label><Input type="email" value={verifyEmail} onChange={(e) => setVerifyEmail(e.target.value)} placeholder="ornek@eposta.com" className="rounded-xl mt-1" data-testid="phone-email-input" /></div>
                {!otpSent ? (
                  <Button onClick={requestPhone} disabled={!phone || !verifyEmail} className="brand-gradient text-white rounded-xl" data-testid="phone-request-button">E-Posta ile Kod Gönder</Button>
                ) : (
                  <>
                    <div className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl p-3" data-testid="phone-email-notice">
                      <BadgeCheck className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>SMS maliyetsiz / güvenli onay: <strong>{verifyEmail}</strong> adresinize gönderilen kodu girerek telefon numaranızı doğrulayın.</span>
                    </div>
                    <div><Label>Doğrulama Kodu</Label><Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="6 haneli kod" className="rounded-xl mt-1" data-testid="phone-otp-input" /></div>
                    <div className="flex items-center gap-3">
                      <Button onClick={verifyPhone} disabled={otp.length !== 6} className="brand-gradient text-white rounded-xl" data-testid="phone-verify-button">Doğrula</Button>
                      <Button variant="outline" onClick={requestPhone} disabled={resendIn > 0} className="rounded-xl" data-testid="phone-resend-button">
                        {resendIn > 0 ? `Tekrar gönder (${resendIn}s)` : "Kodu tekrar gönder"}
                      </Button>
                    </div>
                    {resendIn > 0 && <p className="text-xs text-slate-400" data-testid="phone-countdown">Yeni kod isteyebilmek için {resendIn} saniye bekleyin.</p>}
                  </>
                )}
                <p className="text-xs text-slate-400">Telefon numaranız gizli tutulur, yalnızca iletişim açan yetkili ustaya gösterilir.</p>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="usta" className="mt-5">
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-lg">
            {ustaProfile === undefined ? (
              <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
            ) : hasUstaProfile ? (
              <div className="text-center py-4" data-testid="usta-exists-card">
                <h3 className="font-bold text-slate-900">Usta profiliniz mevcut</h3>
                <p className="text-sm text-slate-500 mt-1 mb-4">Vitrininizi görüntüleyin veya düzenleyin.</p>
                <Button onClick={() => navigate(`/usta/${user.id}`)} className="brand-gradient text-white rounded-xl" data-testid="go-usta-vitrin">Usta Vitrinime Git →</Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-slate-900">{ustaProfile ? "Usta Profilini Düzenle" : "Usta Profili Oluştur"}</h3>
                    <p className="text-sm text-slate-500">{ustaProfile ? "Vitrininizi güncelleyin." : "Formu doldurun, hesabınız usta statüsü kazansın."}</p>
                  </div>
                  {ustaProfile && <button onClick={() => navigate(`/usta/${user.id}`)} className="text-sm font-semibold text-emerald-600" data-testid="view-my-usta-profile">Vitrini Gör →</button>}
                </div>
                <UstaProfileForm initial={ustaProfile || {}} onSaved={() => api.get(`/ustalar/${user.id}`).then(({ data }) => setUstaProfile(data))} />
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="mt-5">
          <div className="space-y-3">
            {jobs.length === 0 ? <Empty text="Henüz ilanınız yok." /> : jobs.map((j) => (
              <div key={j.id} className="bg-white rounded-2xl border border-slate-200/80 p-4 flex items-center justify-between" data-testid={`myjob-${j.id}`}>
                <div><Link to={`/ilan/${j.id}`} className="font-bold text-slate-900 hover:text-emerald-600">{j.title}</Link><div className="text-xs text-slate-400">{j.category_name} • {j.offer_count} teklif • {STATUS_LABELS[j.status]}</div></div>
                <Link to={`/ilan/${j.id}`} className="text-sm text-emerald-600 font-semibold">Teklifleri Gör →</Link>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="offers" className="mt-5">
          <div className="space-y-3">
            {offers.length === 0 ? <Empty text="Henüz teklifiniz yok." /> : offers.map((o) => (
              <div key={o.id} className="bg-white rounded-2xl border border-slate-200/80 p-4 flex items-center justify-between">
                <div><Link to={`/ilan/${o.job_id}`} className="font-bold text-slate-900 hover:text-emerald-600">{o.job_title}</Link><div className="text-xs text-slate-400">{o.is_over ? "1.000.000+ ₺" : tl(o.price)} • {o.duration_days} gün</div></div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${o.status === "accepted" ? "bg-emerald-50 text-emerald-700" : o.status === "rejected" ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-500"}`}>{o.status === "accepted" ? "Kabul edildi" : o.status === "rejected" ? "Reddedildi" : o.status === "pending" ? "Beklemede" : o.status}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="unlocks" className="mt-5">
          <div className="space-y-3">
            {unlocks.length === 0 ? <Empty text="Henüz iletişim açmadınız." /> : unlocks.map((u) => (
              <div key={u.id} className="bg-white rounded-2xl border border-slate-200/80 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3"><PhoneCall className="h-5 w-5 text-emerald-500" /><div><Link to={`/ilan/${u.job_id}`} className="font-bold text-slate-900">{u.job_title}</Link><div className="text-xs text-slate-400">{new Date(u.unlocked_at).toLocaleString("tr-TR")}</div></div></div>
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">Açıldı</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="favorites" className="mt-5">
          <div className="grid sm:grid-cols-2 gap-3">
            {favs.jobs.length === 0 && favs.ustalar.length === 0 ? <Empty text="Favoriniz yok." /> : (<>
              {favs.jobs.map((j) => <Link key={j.id} to={`/ilan/${j.id}`} className="bg-white rounded-2xl border border-slate-200/80 p-4"><div className="font-bold text-slate-900">{j.title}</div><div className="text-xs text-slate-400">{j.category_name}</div></Link>)}
              {favs.ustalar.map((u) => <Link key={u.user_id} to={`/usta/${u.user_id}`} className="bg-white rounded-2xl border border-slate-200/80 p-4"><div className="font-bold text-slate-900">{u.name}</div><div className="text-xs text-slate-400">{u.specialty}</div></Link>)}
            </>)}
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-5">
          <div className="space-y-2">
            {notifs.length === 0 ? <Empty text="Bildirim yok." /> : notifs.map((n) => (
              <button key={n.id} onClick={() => navigate(n.link || "/mesajlar")} data-testid={`notif-${n.id}`}
                className="w-full text-left bg-white rounded-2xl border border-slate-200/80 p-4 hover:border-emerald-300 transition-colors">
                <div className="font-semibold text-slate-900 text-sm">{n.title}</div>
                <div className="text-xs text-slate-500">{n.body}</div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-5">
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-lg space-y-4">
            <div>
              <h3 className="font-bold text-slate-900">KVKK & Gizlilik</h3>
              <p className="text-sm text-slate-500 mt-1">Kişisel bilgileriniz KVKK kapsamında korunur. Ustayla taraflar arasındaki hizmet bedelini tahsil etmez.</p>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <h3 className="font-bold text-red-600">Hesabı Sil</h3>
              <p className="text-sm text-slate-500 mt-1 mb-3">Hesabınız silinir; yasal saklama gereği finansal işlem kayıtları korunur.</p>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="destructive" className="rounded-xl" data-testid="delete-account-button">Hesabı Sil</Button></AlertDialogTrigger>
                <AlertDialogContent className="rounded-2xl">
                  <AlertDialogHeader><AlertDialogTitle>Emin misiniz?</AlertDialogTitle><AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel><AlertDialogAction onClick={deleteAccount} className="rounded-xl bg-red-600" data-testid="confirm-delete-account">Evet, Sil</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty({ text }) {
  return <div className="bg-white rounded-2xl border border-slate-200/80 p-10 text-center text-slate-400 text-sm">{text}</div>;
}
